// Libs
import classNames from 'classnames/bind';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Stack,
} from '@mui/material';
// Components, Layouts, Pages
import { BaseCheckbox, BaseDynamicSelect, BaseInput, BaseLabel, BaseSelect, Loading } from '~/components/common';
// Others
import { IFormEquipment } from '~/utils/interface/equipment';
import { useAppDispatch } from '~/utils/redux/hooks';
import { defaultValuesEquipmentForm, schema } from './helper';
import { compareDataUpdate, removeEmptyObjects } from '~/utils/helper/common';
import { IBaseOption, IBodyUpdate, IResultCallApiFn } from '~/utils/interface/common';
import { createEquipment, getDetailEquipment, updateEquipment } from '~/thunks/equipment/equipmentThunk';
import {
  EMPTY_STRING,
  optionTrailerType,
  commonOptionStatus,
  optionRentalStatus,
  optionEquipmentType,
} from '~/utils/constants/common';
// Styles, images, icons
import styles from './EquipmentForm.module.scss';
import { icons } from '~/assets';

type Props = {
  idEquipment?: string;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const EquipmentForm = (props: Props) => {
  //#region Destructuring Props
  const { idEquipment, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/equipment', 'common/base']);
  const dispatch = useAppDispatch();
  const {
    control,
    setValue,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<IFormEquipment>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesEquipmentForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [initialData, setInitialData] = useState<IFormEquipment>(defaultValuesEquipmentForm);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!idEquipment) return;

    handleGetDetailEquipment(idEquipment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idEquipment]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailEquipment = (idEquipment: string) => {
    setIsLoading(true);

    dispatch(getDetailEquipment(idEquipment))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { id, ...remainingData } = res?.data;
        const body: IFormEquipment = {
          ...remainingData,
          name: remainingData?.name || EMPTY_STRING,
          rentalStatus: remainingData?.rentalStatus || EMPTY_STRING,
          status: remainingData?.status || EMPTY_STRING,
          note: remainingData?.note || EMPTY_STRING,
          equipmentType: remainingData?.equipmentType || EMPTY_STRING,
          isPairingTrailer: remainingData?.isPairingTrailer || false,
          trailerType: remainingData?.trailerType || [],
        };

        setInitialData(body);

        reset(body);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleFormEquipment = (data: IFormEquipment) => {
    if (idEquipment) {
      handleEditEquipment(data);
    } else {
      handleAddEquipment(data);
    }

    setIsLoading(true);
  };

  const handleAddEquipment = (data: IFormEquipment) => {
    const convertData = removeEmptyObjects(data) as IFormEquipment;

    dispatch(createEquipment(convertData))
      .unwrap()
      .then((res) => {
        handleCloseModal();
      })
      .catch((err) => {})
      .finally(() => setIsLoading(false));
  };

  const handleEditEquipment = async (data: IFormEquipment) => {
    const dataChange = compareDataUpdate(initialData, data) as IFormEquipment;

    const convertData: IFormEquipment = {
      ...dataChange,
      trailerType: data.trailerType,
    };

    const params: IBodyUpdate<IFormEquipment> = {
      id: idEquipment || EMPTY_STRING,
      body: convertData,
    };

    dispatch(updateEquipment(params))
      .unwrap()
      .then(() => {
        handleCloseModal();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseModal = () => {
    onClose && onClose();
    reset();
  };

  const fetchOptionsRentalStatus = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionRentalStatus,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionsStatus = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: commonOptionStatus,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchDataTrailerList = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionTrailerType,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionsEquipmentType = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionEquipmentType,
      hasMoreResp: false,
    };

    return result;
  }, []);
  //#endregion Handle Function

  return (
    <Dialog
      id='equipmentFormModalComponent'
      aria-labelledby='equipmentFormModal'
      open={true}
      onClose={handleCloseModal}
      fullWidth
      maxWidth={'md'}
    >
      <DialogTitle id='equipmentFormModal' className={cx('header')}>
        <Stack direction={'column'} spacing={'10px'}>
          <Stack direction={'row'} spacing={10}>
            <BaseLabel
              label={t(idEquipment ? 'form_equipment_title_edit' : 'form_equipment_title')}
              styleType='bigTitle'
            />
          </Stack>
        </Stack>

        <IconButton onClick={handleCloseModal} className={cx('buttonClose')}>
          <Avatar
            src={icons.commonIconCancel}
            className={cx('iconClose')}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent className={cx('content')} dividers>
        <Grid2 columnSpacing={'24px'} rowSpacing={'24px'} container>
          <Grid2 size={6}>
            <Controller
              control={control}
              name={'name'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    label={t('form_equipment_title_name')}
                    placeholder={t('form_equipment_name_placeholder')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.name?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'equipmentType'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseSelect
                    gap={10}
                    height={36}
                    styleType='contained'
                    label={t('form_equipment_equipment_type')}
                    placeholder={t('form_equipment_type_placeholder')}
                    callApiFn={fetchOptionsEquipmentType}
                    value={value}
                    onChange={(data) => onChange(data?.value)}
                    errorMessage={errors?.equipmentType?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'status'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseSelect
                    gap={10}
                    height={36}
                    styleType='contained'
                    label={t('form_equipment_status')}
                    placeholder={t('form_equipment_status_placeholder')}
                    callApiFn={fetchOptionsStatus}
                    value={value}
                    onChange={(data) => onChange(data?.value)}
                    errorMessage={errors?.status?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={12}>
            <Controller
              control={control}
              name={'isPairingTrailer'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseCheckbox
                    label={t('form_equipment_pairing_trailer')}
                    checked={value}
                    onChange={(value) => onChange(value)}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'trailerType'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseDynamicSelect
                    multiple={true}
                    label={t('form_equipment_title_trailer_type')}
                    callApiFn={fetchDataTrailerList}
                    value={value}
                    onChange={(selected) => {
                      setValue(
                        'trailerType',
                        selected.optionsSelected.map((opt) => opt.value)
                      );
                      onChange(selected.idSelected);
                    }}
                    errorMessage={errors?.trailerType?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <Controller
              control={control}
              name={'rentalStatus'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseSelect
                    gap={10}
                    height={36}
                    styleType='contained'
                    label={t('form_equipment_rental_status')}
                    placeholder={t('form_equipment_rental_status_placeholder')}
                    callApiFn={fetchOptionsRentalStatus}
                    value={value}
                    onChange={(data) => onChange(data?.value)}
                    errorMessage={errors?.rentalStatus?.message}
                  />
                );
              }}
            />
          </Grid2>

          <Grid2 size={12}>
            <Controller
              control={control}
              name={'note'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    multiline
                    rows={7}
                    height={'auto'}
                    label={t('form_equipment_note')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.note?.message}
                  />
                );
              }}
            />
          </Grid2>
        </Grid2>
      </DialogContent>

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCloseModal}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button
          variant='contained'
          className={cx('buttonSave')}
          onClick={handleSubmit(handleFormEquipment)}
          disabled={idEquipment ? !isDirty : false}
        >
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>

      {isLoading && <Loading />}
    </Dialog>
  );
};

export default EquipmentForm;
